import React, { Fragment, useState } from 'react';
import { MenuList, MenuItem } from '@material-ui/core';
import { Archive, Bell, BellOff, FileText, Trash } from 'react-feather';
import { useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Confirmation } from 'src/components/Tables/ItemListTable/components';
import { SnackbarAlert } from 'src/components/Alerts/components/System/components/SnackbarAlert';
import { DEVELOPER_LEVEL_ACCESS } from 'src/components/access';
import { useWarboxConversation, useWarboxMessage } from 'src/hooks';
import useAuth from 'src/hooks/useAuth';

type TConfirmAction = 'clear' | 'delete' | null;

export const OptionsMenu = ({
    conversationId,
    isSilenced = false,
    onConversationDeleted,
}: {
    conversationId: string;
    isSilenced?: boolean;
    onConversationDeleted?: () => void;
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { updateConversation, deleteConversation } = useWarboxConversation();
    const { clearConversationMessages } = useWarboxMessage();
    const { customAlert } = SnackbarAlert();
    const [confirmAction, setConfirmAction] = useState<TConfirmAction>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isDeveloper = user?.accessLevel === DEVELOPER_LEVEL_ACCESS;

    const handleToggleNotifications = async () => {
        if (!conversationId || isSubmitting) return;

        setIsSubmitting(true);
        try {
            await updateConversation(
                { isSilenced: !isSilenced },
                conversationId
            );
            customAlert(
                isSilenced
                    ? t('Chat.NotificationsUnmuted')
                    : t('Chat.NotificationsMuted'),
                'success'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirm = async (confirmed: boolean) => {
        const action = confirmAction;
        setConfirmAction(null);

        if (!confirmed || !action || !conversationId || isSubmitting) return;

        setIsSubmitting(true);
        try {
            if (action === 'clear') {
                await clearConversationMessages(conversationId);
                customAlert(t('SnackBar.ConversationCleared'), 'success');
                return;
            }

            await deleteConversation(conversationId);
            customAlert(t('SnackBar.ConversationDeleted'), 'error');
            onConversationDeleted?.();
        } catch (error) {
            customAlert(
                error instanceof Error
                    ? error.message
                    : t('Errors.ScoreError'),
                'error'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const options = [
        {
            icon: isSilenced ? <Bell /> : <BellOff />,
            label: isSilenced
                ? t('Chat.UnmuteNotifications')
                : t('Chat.MuteNotifications'),
            onClick: handleToggleNotifications,
            showIf: true,
        },
        {
            icon: <Archive />,
            label: t('Chat.ClearChat'),
            color: theme.palette.error.main,
            onClick: () => setConfirmAction('clear'),
            showIf: isDeveloper,
        },
        {
            icon: <Trash />,
            label: t('Chat.DeleteChat'),
            color: theme.palette.error.main,
            onClick: () => setConfirmAction('delete'),
            showIf: isDeveloper,
        },
    ];

    const confirmMessage =
        confirmAction === 'clear'
            ? t('Chat.ClearChatConfirm')
            : t('Chat.DeleteChatConfirm');

    return (
        <Fragment>
            <MenuList>
                {options
                    .filter((option) => option.showIf)
                    .map((option, index) => (
                        <MenuItem
                            key={index}
                            onClick={option.onClick}
                            disabled={isSubmitting}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '15px 10px',
                                color: option.color || theme.palette.text.primary,
                                transition: '0.4s',
                            }}
                        >
                            {option.icon} {option.label}
                        </MenuItem>
                    ))}
            </MenuList>

            <Confirmation
                open={Boolean(confirmAction)}
                setOpen={(open) => {
                    if (!open) setConfirmAction(null);
                }}
                message={confirmMessage}
                handleConfirm={handleConfirm}
            />
        </Fragment>
    );
};
